<template>
  <div
    class="test-case-item group relative border-l-4 rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
    :class="[getBorderClass(), getBackgroundClass(), getItemStateClass()]"
    :style="{ animationDelay: `${index * 100}ms` }"
  >
    <div class="mb-2 flex items-start justify-between">
      <div class="flex flex-1 items-center min-w-0 space-x-3">
        <!-- 选择框 -->
        <a-checkbox
          :checked="selected"
          :disabled="adopted"
          class="flex-shrink-0"
          @change="handleSelectChange"
          @click.stop
        />

        <check-circle-outlined :class="getIconClass()" class="flex-shrink-0" />
        <span
          class="cursor-pointer truncate text-sm font-medium text-gray-800"
          :title="testCase.test_case_name"
          @click="emit('viewDetail', testCase)"
        >
          {{ testCase.test_case_name }}
        </span>
      </div>
      <div class="flex flex-shrink-0 items-center space-x-2">
        <span class="rounded px-2 py-1 text-xs" :class="getPriorityClass()">
          {{ getPriorityText() }}
        </span>

        <!-- 采纳状态或图标 -->
        <div class="flex items-center">
          <div
            v-if="!adopted"
            class="group/adopt h-7 w-7 flex cursor-pointer items-center justify-center border border-blue-200 rounded-full bg-blue-50 text-blue-600 transition-all duration-200 hover:scale-110 hover:border-blue-300 hover:bg-blue-100"
            :title="adopting ? '采纳中...' : '点击采纳'"
            @click.stop="handleAdopt"
          >
            <plus-outlined v-if="!adopting" class="text-sm" />
            <icon-vue v-else icon="mdi:loading" class="animate-spin text-sm" />
          </div>
          <div
            v-else
            class="h-7 w-7 flex items-center justify-center border border-green-200 rounded-full bg-green-100 text-green-600 shadow-sm"
            title="已采纳"
          >
            <check-outlined class="text-sm" />
          </div>
        </div>

        <a-button
          size="small"
          type="text"
          class="text-gray-400 hover:text-gray-600"
          @click.stop="emit('viewDetail', testCase)"
        >
          <template #icon><right-outlined /></template>
        </a-button>
      </div>
    </div>

    <p class="line-clamp-2 mb-2 text-xs text-gray-500" :title="testCase.test_case_summary">
      {{ testCase.test_case_summary || '暂无描述' }}
    </p>

    <div class="flex items-center justify-between text-xs">
      <div class="flex items-center space-x-4">
        <div class="flex items-center space-x-1">
          <aim-outlined class="text-blue-500" />
          <span class="text-gray-600">关联度</span>
          <span class="font-medium text-blue-600">{{ getCorrelationScore() }}</span>
        </div>
        <div v-if="testCase.estimated_execution_time" class="flex items-center space-x-1">
          <clock-circle-outlined class="text-green-500" />
          <span class="text-gray-600">预计</span>
          <span class="font-medium text-green-600">{{ testCase.estimated_execution_time }}min</span>
        </div>
      </div>
      <div v-if="testCase.risk_score" class="flex items-center space-x-1">
        <span class="text-gray-500">风险</span>
        <span class="font-medium" :class="getRiskScoreClass()">{{ getRiskScore() }}</span>
      </div>
    </div>

    <!-- 标签 -->
    <div v-if="testCase.tags && testCase.tags.length > 0" class="mt-2 flex flex-wrap gap-1">
      <span
        v-for="tag in testCase.tags.slice(0, 3)"
        :key="tag"
        class="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
      >
        {{ tag }}
      </span>
      <span v-if="testCase.tags.length > 3" class="text-xs text-gray-400">+{{ testCase.tags.length - 3 }}</span>
    </div>

    <!-- 相关文件 -->
    <div v-if="testCase.related_files && testCase.related_files.length > 0" class="mt-2">
      <div class="mb-1 text-xs text-gray-500">相关文件:</div>
      <div class="flex flex-wrap gap-1">
        <span
          v-for="file in testCase.related_files.slice(0, 2)"
          :key="file"
          class="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-xs font-mono text-blue-700"
        >
          {{ getFileName(file) }}
        </span>
        <span v-if="testCase.related_files.length > 2" class="text-xs text-gray-400">
          +{{ testCase.related_files.length - 2 }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TestCaseCorrelation } from '@/types/precision-new/ai-analysis';
import {
  AimOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons-vue';
import { Icon as IconVue } from '@iconify/vue';
import { ref } from 'vue';

// Props
interface Props {
  testCase: TestCaseCorrelation;
  index: number;
  selected?: boolean;
  adopted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  adopted: false,
});

// Emits
const emit = defineEmits(['viewDetail', 'select', 'adopt']);

// 状态
const adopting = ref(false);

// Computed
const getPriorityClass = () => {
  const priorityClasses = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800',
  };
  return (
    priorityClasses[props.testCase.test_case_priority as keyof typeof priorityClasses] || 'bg-gray-100 text-gray-800'
  );
};

const getPriorityText = () => {
  const priorityTexts = {
    critical: '关键',
    high: '高',
    medium: '中',
    low: '低',
  };
  return priorityTexts[props.testCase.test_case_priority as keyof typeof priorityTexts] || '未知';
};

const getBorderClass = () => {
  const borderClasses = {
    critical: 'border-red-500',
    high: 'border-orange-500',
    medium: 'border-yellow-500',
    low: 'border-green-500',
  };
  return borderClasses[props.testCase.test_case_priority as keyof typeof borderClasses] || 'border-blue-500';
};

const getBackgroundClass = () => {
  const backgroundClasses = {
    critical: 'bg-red-50 hover:bg-red-100',
    high: 'bg-orange-50 hover:bg-orange-100',
    medium: 'bg-yellow-50 hover:bg-yellow-100',
    low: 'bg-green-50 hover:bg-green-100',
  };
  return (
    backgroundClasses[props.testCase.test_case_priority as keyof typeof backgroundClasses] ||
    'bg-blue-50 hover:bg-blue-100'
  );
};

const getIconClass = () => {
  const iconClasses = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-yellow-600',
    low: 'text-green-600',
  };
  return iconClasses[props.testCase.test_case_priority as keyof typeof iconClasses] || 'text-blue-600';
};

const getCorrelationScore = () => {
  return `${Math.round((props.testCase.correlation_score || 0) * 100)}%`;
};

const getRiskScore = () => {
  if (!props.testCase.risk_score) {
    return '-';
  }
  return `${Math.round(props.testCase.risk_score * 100)}%`;
};

const getRiskScoreClass = () => {
  if (!props.testCase.risk_score) {
    return 'text-gray-500';
  }
  const score = props.testCase.risk_score;
  if (score >= 0.8) {
    return 'text-red-600';
  }
  if (score >= 0.6) {
    return 'text-orange-600';
  }
  if (score >= 0.4) {
    return 'text-yellow-600';
  }
  return 'text-green-600';
};

const getFileName = (filePath: string) => {
  return filePath.split('/').pop() || filePath;
};

// 新增方法
const handleSelectChange = (e: any) => {
  emit('select', props.testCase.test_case_id, e.target.checked);
};

const handleAdopt = async () => {
  adopting.value = true;
  try {
    emit('adopt', props.testCase.test_case_id);
  } finally {
    // 这里不立即设置为false，让父组件控制loading状态
    setTimeout(() => {
      adopting.value = false;
    }, 1000);
  }
};

const getItemStateClass = () => {
  if (props.adopted) {
    return 'adopted-item';
  }
  if (props.selected) {
    return 'selected-item';
  }
  return '';
};
</script>

<style scoped>
.test-case-item {
  animation: fadeInUp 0.5s ease-out;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-clamp: 2;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 选中状态样式 */
.selected-item {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%) !important;
  border-left-color: #3b82f6 !important;
  box-shadow:
    0 0 0 2px rgba(59, 130, 246, 0.2),
    0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-1px);
}

/* 已采纳状态样式 */
.adopted-item {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%) !important;
  border-left-color: #22c55e !important;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.1);
  position: relative;
}

.adopted-item::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 20px 20px 0;
  border-color: transparent #22c55e transparent transparent;
  opacity: 0.1;
}

.adopted-item:hover {
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.2);
}

/* 悬浮效果增强 */
.test-case-item:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.test-case-item:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}
</style>
